import * as s from '~js/utils/selectors';
import * as a from '~js/utils/attributes';
import * as c from '~js/utils/classes';
import * as stngs from '~js/utils/settings';
import { throttle } from '~js/utils/utils';

export class Collapsible {
  constructor(container) {
    this.container = container;
    this.single = this.container.querySelector(s.selectors$15.single);
    this.triggers = this.container.querySelectorAll(s.selectors$15.trigger);
    this.resetHeightTimer = 0;
    this.isTransitioning = false;
    this.transitionOverride = this.container.hasAttribute(a.attributes$J.transitionOverride);
    this.collapsibleToggleEvent = (event) => throttle(this.collapsibleToggle(event), 1250);

    this.init();
  }

  init() {
    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', this.collapsibleToggleEvent);
      trigger.addEventListener('keyup', this.collapsibleToggleEvent);
    });
  }

  collapsibleToggle(e) {
    e.preventDefault();

    const trigger = e.target.matches(s.selectors$15.trigger) ? e.target : e.target.closest(s.selectors$15.trigger);
    const dropdownId = trigger.getAttribute(a.attributes$J.controls);
    const dropdown = document.getElementById(dropdownId);
    const triggerMobile = trigger.hasAttribute(a.attributes$J.triggerMobile);
    const isExpanded = trigger.classList.contains(c.classes$V.isExpanded);
    const isSpace = e.code === theme.keyboardKeys.SPACE;
    const isEscape = e.code === theme.keyboardKeys.ESCAPE;
    const isMobile = window.innerWidth < theme.sizes.small;

    // Do nothing if transitioning
    if (this.isTransitioning && !this.transitionOverride) {
      return;
    }

    // Do nothing if any different than ESC and Space key pressed
    if (e.code && !isSpace && !isEscape) {
      return;
    }

    // Do nothing if ESC key pressed and not expanded or mobile trigger clicked and screen not mobile
    if ((!isExpanded && isEscape) || (triggerMobile && !isMobile)) {
      return;
    }

    this.isTransitioning = true;
    trigger.disabled = true;

    // When we want only one item expanded at the same time
    if (this.single) {
      this.triggers.forEach((otherTrigger) => {
        const isExpanded = otherTrigger.classList.contains(c.classes$V.isExpanded);

        if (trigger == otherTrigger || !isExpanded) return;

        const dropdownId = otherTrigger.getAttribute(a.attributes$J.controls);
        const dropdown = document.getElementById(dropdownId);

        requestAnimationFrame(() => {
          this.closeItem(dropdown, otherTrigger);
        });
      });
    }

    // requestAnimationFrame fixes content jumping when item is sliding down
    if (isExpanded) {
      requestAnimationFrame(() => {
        this.closeItem(dropdown, trigger);
      });
    } else {
      requestAnimationFrame(() => {
        this.openItem(dropdown, trigger);
      });
    }
  }

  openItem(dropdown, trigger) {
    let dropdownHeight = dropdown.querySelector(s.selectors$15.content).offsetHeight;

    this.setDropdownHeight(dropdown, dropdownHeight, trigger, true);
    trigger.classList.add(c.classes$V.isExpanded);
    trigger.setAttribute(a.attributes$J.expanded, true);

    trigger.dispatchEvent(
      new CustomEvent('theme:form:sticky', {
        bubbles: true,
        detail: {
          element: 'accordion',
        },
      })
    );
  }

  closeItem(dropdown, trigger) {
    let dropdownHeight = dropdown.querySelector(s.selectors$15.content).offsetHeight;

    requestAnimationFrame(() => {
      dropdownHeight = 0;
      this.setDropdownHeight(dropdown, dropdownHeight, trigger, false);
      trigger.classList.remove(c.classes$V.isExpanded);
    });

    this.setDropdownHeight(dropdown, dropdownHeight, trigger, false);
    trigger.classList.remove(c.classes$V.isExpanded);
    trigger.setAttribute(a.attributes$J.expanded, false);
  }

  setDropdownHeight(dropdown, dropdownHeight, trigger, isExpanded) {
    dropdown.style.height = `${dropdownHeight}px`;
    dropdown.setAttribute(a.attributes$J.expanded, isExpanded);
    dropdown.classList.toggle(c.classes$V.isExpanded, isExpanded);

    if (this.resetHeightTimer) {
      clearTimeout(this.resetHeightTimer);
    }

    if (dropdownHeight == 0) {
      this.resetHeightTimer = setTimeout(() => {
        dropdown.style.height = '';
      }, stngs.settings$9.animationDelay);
    }

    if (isExpanded) {
      this.resetHeightTimer = setTimeout(() => {
        dropdown.style.height = 'auto';
        this.isTransitioning = false;
      }, stngs.settings$9.animationDelay);
    } else {
      this.isTransitioning = false;
    }

    // Always remove trigger disabled attribute after animation completes
    setTimeout(() => {
      trigger.disabled = false;
    }, stngs.settings$9.animationDelay);
  }

  onUnload() {
    this.triggers.forEach((trigger) => {
      trigger.removeEventListener('click', this.collapsibleToggleEvent);
      trigger.removeEventListener('keyup', this.collapsibleToggleEvent);
    });
  }
}